<template>
  <div v-editable="blok" class="grid" :class="gridClass">
    <div
      v-for="(columnBloks, columnIndex) in gridColumns"
      :key="`column-${columnIndex}`"
      class="grid__column"
    >
      <StoryblokComponent
        v-for="currentBlok in columnBloks"
        :key="currentBlok._uid"
        :blok="currentBlok"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SbBlokData } from '@storyblok/vue';
import { StoryblokComponent } from '@storyblok/vue';
import { layoutColumnKeys, GridLayout } from '@/features/storyblok/constants';
import type { StoryblokGridBlok } from '@/features/storyblok/types';
import { computed } from 'vue';

const props = defineProps<{
  blok: StoryblokGridBlok;
}>();

const normalizedLayout = computed<GridLayout>(() => {
  const layout = props.blok.layout;
  if (
    layout === GridLayout['cols-1'] ||
    layout === GridLayout['cols-2'] ||
    layout === GridLayout['cols-30-70'] ||
    layout === GridLayout['cols-70-30'] ||
    layout === GridLayout['cols-25-75'] ||
    layout === GridLayout['cols-75-25'] ||
    layout === GridLayout['cols-3'] ||
    layout === GridLayout['cols-4']
  ) {
    return layout;
  }

  return GridLayout['cols-1'];
});

const gridColumns = computed<SbBlokData[][]>(() => {
  if (Array.isArray(props.blok.columns)) {
    return [props.blok.columns];
  }

  return layoutColumnKeys[normalizedLayout.value].map((columnKey) => {
    const columnValue = props.blok[columnKey];
    return Array.isArray(columnValue) ? columnValue : [];
  });
});

const gridClass = computed(() => `grid--${normalizedLayout.value}`);
</script>

<style scoped>
.grid {
  display: grid;
  gap: 16px;
}

.grid--cols-1 {
  grid-template-columns: 1fr;
}

.grid--cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.grid--cols-30-70 {
  grid-template-columns: 3fr 7fr;
}

.grid--cols-70-30 {
  grid-template-columns: 7fr 3fr;
}

.grid--cols-25-75 {
  grid-template-columns: 1fr 3fr;
}

.grid--cols-75-25 {
  grid-template-columns: 3fr 1fr;
}

.grid--cols-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.grid--cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

@media (max-width: 768px) {
  .grid--cols-2,
  .grid--cols-30-70,
  .grid--cols-70-30,
  .grid--cols-25-75,
  .grid--cols-75-25,
  .grid--cols-3,
  .grid--cols-4 {
    grid-template-columns: 1fr;
  }
}
</style>
